import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'
import MenuRoundedIcon from '@mui/icons-material/MenuRounded'
import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import Logo from '../components/Logo'
import { useAuth } from '../auth/AuthProvider'

/** Chrome for the admin console: top bar, brand, "Admin" badge, user menu.
 *
 * On desktop the nav links sit inline in the toolbar. Below `md` they collapse
 * into a hamburger → Drawer so the header doesn't overflow on phones. */
export default function AdminShell({ children }: { children: ReactNode }) {
  const { name, logout, authEnabled, isAuthenticated, hasRole, devassistProjectUrl } =
    useAuth()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  // Only true Admins see the DevAssist deep-link, and only when configured.
  // No token is forwarded — DevAssist authenticates the user via Entra SSO.
  const showDevAssist = hasRole(['Admin']) && Boolean(devassistProjectUrl)
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const initials =
    name
      .split(' ')
      .map((p) => p[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || 'VE'

  const signedIn = authEnabled && isAuthenticated

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="sticky"
        elevation={0}
        color="inherit"
        sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}
      >
        <Toolbar sx={{ gap: 1.5 }}>
          {isMobile && (
            <IconButton
              edge="start"
              aria-label="Open menu"
              onClick={() => setDrawerOpen(true)}
            >
              <MenuRoundedIcon />
            </IconButton>
          )}
          <Box component={Link} to="/admin" sx={{ display: 'flex', alignItems: 'center' }}>
            <Logo />
          </Box>
          <Chip
            label="Admin"
            size="small"
            sx={{ fontWeight: 700, bgcolor: 'rgba(0,117,188,0.1)', color: 'primary.main' }}
          />

          {/* Desktop: inline nav + actions. */}
          {!isMobile && (
            <>
              <Button size="small" component={Link} to="/admin" sx={{ color: 'text.secondary' }}>
                Events
              </Button>
              <Button size="small" component={Link} to="/admin/templates" sx={{ color: 'text.secondary' }}>
                Templates
              </Button>
              <Button size="small" component={Link} to="/admin/people" sx={{ color: 'text.secondary' }}>
                People
              </Button>
              <Button size="small" component={Link} to="/admin/settings" sx={{ color: 'text.secondary' }}>
                Settings
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              {showDevAssist && (
                <Button
                  size="small"
                  component="a"
                  href={devassistProjectUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<LaunchRoundedIcon />}
                  sx={{ color: 'text.secondary' }}
                >
                  Open in DevAssist
                </Button>
              )}
              <Button
                size="small"
                component={Link}
                to="/app"
                endIcon={<LaunchRoundedIcon />}
                sx={{ color: 'text.secondary' }}
              >
                Attendee view
              </Button>
            </>
          )}

          {/* Mobile: push the avatar to the right. */}
          {isMobile && <Box sx={{ flexGrow: 1 }} />}

          {signedIn && (
            <>
              <Avatar
                onClick={(e) => setAnchor(e.currentTarget)}
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: 'primary.main',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {initials}
              </Avatar>
              <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Signed in
                  </Typography>
                </Box>
                <MenuItem
                  onClick={() => {
                    setAnchor(null)
                    logout()
                  }}
                >
                  Sign out
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile navigation drawer. */}
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 260 }} role="presentation">
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Logo />
            <Chip
              label="Admin"
              size="small"
              sx={{ fontWeight: 700, bgcolor: 'rgba(0,117,188,0.1)', color: 'primary.main' }}
            />
          </Box>
          <Divider />
          <List onClick={() => setDrawerOpen(false)}>
            <ListItemButton component={Link} to="/admin">
              <ListItemText primary="Events" />
            </ListItemButton>
            <ListItemButton component={Link} to="/admin/templates">
              <ListItemText primary="Templates" />
            </ListItemButton>
            <ListItemButton component={Link} to="/admin/people">
              <ListItemText primary="People" />
            </ListItemButton>
            <ListItemButton component={Link} to="/admin/settings">
              <ListItemText primary="Settings" />
            </ListItemButton>
            <ListItemButton component={Link} to="/app">
              <ListItemText primary="Attendee view" />
            </ListItemButton>
            {showDevAssist && (
              <ListItemButton
                component="a"
                href={devassistProjectUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ListItemText primary="Open in DevAssist" />
              </ListItemButton>
            )}
          </List>
          {signedIn && (
            <>
              <Divider />
              <List>
                <ListItemButton
                  onClick={() => {
                    setDrawerOpen(false)
                    logout()
                  }}
                >
                  <ListItemText primary="Sign out" />
                </ListItemButton>
              </List>
            </>
          )}
        </Box>
      </Drawer>

      <Box component="main">{children}</Box>
    </Box>
  )
}
